import { FC, useEffect, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { contrast, deepCopy, getItemLabelFromObjectArray, isEqual, isUndef } from '@lhb/func';
import { getFranchiseeSelection } from '@/common/api/expandStore/franchisee';
// import styles from './index.module.less';
import { Col, Form, Row } from 'antd';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import FormSetName from '@/common/components/Form/FormSetName';

/** 拓店状态枚举 */
enum StoreExpansionStatus {
  /** 匹配商圈 */
  MatchBusinessCircle = 1,
  /** 匹配店铺 */
  MatchStore = 2,
  /** 已签约 */
  Signed = 3,
  /** 无匹配/无动作 */
  NoMatch = 4,
}

/**
 * 动态表单编辑-加盟商合作进展（一鸣）
 * @description
 * @demo
 */
const Component:FC<{
  form: any;
  formItemData: any;
  disabled: boolean;
  required: boolean;
  tiledFormDataRef: any;
  /** 值变更回调，用于实时更新字段到 tiledFormDataRef.current 的 textValue */
  // valueChange: any,
  /** 编辑状态：true 编辑，false 新增 */
  isEdit?: boolean,
  /** 值变更回调，用于实时更新字段到 tiledFormDataRef.current 的 textValue */
  valueChange: any,
}> = ({
  form,
  formItemData,
  disabled,
  required,
  tiledFormDataRef,
  // valueChange,
  isEdit = false,
  valueChange,
}) => {
  /* state */
  const identification = formItemData.identification; // 字段
  const value = Form.useWatch(identification) || {};
  const franchiseeStatus = Form.useWatch('franchiseeStatus', form);

  const [selection, setSelection] = useState<any>({
    storeSequence: getStoreSequenceOptions(),
    cooperationStatus: [],
    storeExpansionStatus: [],
  });

  const storeSequenceProp = [identification, 'storeSequence'];
  const cooperationStatusProp = [identification, 'cooperationStatus'];
  const storeExpansionStatusProp = [identification, 'storeExpansionStatus'];

  /* hooks */
  useEffect(() => {
    methods.getSelection();
  }, []);

  // 新增或编辑时，每次重新渲染动态组件时触发
  useEffect(() => {
    setTimeout(() => { // 避免和 updateValue 同时进行
      // 新增
      if (!isEdit) {
        // 新增时赋予默认值
        form.setFieldValue(storeExpansionStatusProp, StoreExpansionStatus.NoMatch);
      }
    }, 100);

    // 通过 formItemData 获取到编辑详情接口传入的 textValue（不受 updateValue 影响）
    // 这里 textValue 未被解码
    if (isUndef(formItemData?.textValue) || typeof (formItemData?.textValue) !== 'string') return;
    try {
      const textValue = JSON.parse(formItemData?.textValue || '{}');
      // 更新门店序列选项
      setSelection(val => ({
        ...val,
        storeSequence: getStoreSequenceOptions(textValue?.storeSequence || 0),
      }));
    } catch (error) {
      console.log('error', error);
    }
  }, [formItemData]);

  useEffect(() => {
    methods.updateValue();
  }, [value]);

  useEffect(() => {
    methods.updateCooperationStatus();
  }, [franchiseeStatus, selection.cooperationStatus]);

  /* methods */
  const methods = useMethods({

    getSelection() {
      getFranchiseeSelection().then(res => {
        setSelection(val => ({
          ...val,
          cooperationStatus: contrast(res, 'cooperationStatusList', []).map(item => ({ value: item.value, label: item.name })),
          storeExpansionStatus: contrast(res, 'storeExpansionStatusList', []).map(item => ({ value: item.value, label: item.name })),
        }));

        // 通过选项更新 name
        methods.updateValue();
      });
    },

    /** 门店序列变更 */
    changeStoreSequence() {
      // 0122 产品移除该逻辑
      // 变更门店序列时，重置门店状态
      // form.setFieldValue(storeExpansionStatusProp, StoreExpansionStatus.NoMatch);
    },

    /** 根据加盟商状态自动更新合作状态 */
    updateCooperationStatus() {
      const franchiseeStatusItem = tiledFormDataRef?.current?.find((item: any) => item.identification === 'franchiseeStatus');
      const franchiseeStatusOption = franchiseeStatusItem?.propertyConfigOptionVOList?.find((item: any) => item.id === franchiseeStatus);
      /**
       * 加盟商状态和合作状态属于不同字段，选项 id 由后端动态配置，
       * 因此先通过加盟商状态 id 获取中文名称，再用中文映射合作状态，避免硬编码选项 id。
       */
      const cooperationStatusNameMap: Record<string, string> = {
        正常: '正常',
        已签约: '正常',
        暂缓: '暂缓',
        放弃: '放弃',
      };
      const cooperationStatusName = cooperationStatusNameMap[franchiseeStatusOption?.name];
      const cooperationStatusOption = selection.cooperationStatus.find((item: any) => item.label === cooperationStatusName);
      const cooperationStatus = cooperationStatusOption?.value;

      if (!isEqual(form.getFieldValue(cooperationStatusProp), cooperationStatus)) {
        form.setFieldValue(cooperationStatusProp, cooperationStatus);
      }
    },

    /** 更新值 */
    updateValue() {
      /**
       * value 变更时，获取 name 更新到 value 对象字段上
       * value 和后端约定需要包含
       {
        'storeSequence',
        'storeSequenceName',
        'cooperationStatus',
        'cooperationStatusName',
        'storeExpansionStatus',
        'storeExpansionStatusName',
      }
      */
      const _value = deepCopy(value || {});
      if (Object.keys(_value).length) { // 避免空对象
        // 获取名称并更新
        _value.storeSequenceName = getItemLabelFromObjectArray(selection.storeSequence, _value.storeSequence, 'value', 'label');
        _value.cooperationStatusName = getItemLabelFromObjectArray(selection.cooperationStatus, _value.cooperationStatus, 'value', 'label');
        _value.storeExpansionStatusName = getItemLabelFromObjectArray(selection.storeExpansionStatus, _value.storeExpansionStatus, 'value', 'label');

        if (!isEqual(_value, value)) {
          // console.log('更新后的值:', JSON.parse(JSON.stringify(_value)));
          form.setFieldValue(identification, deepCopy(_value));
          valueChange?.(identification, deepCopy(_value));
        }
      }
    },

  });

  return (<Row gutter={24}>

    {/* 需要进行值录入。如果配置表单对同一个字段配置多次，会导致控制台报警（但不用管）；
    用来挂载不在表单项上的字段，如 storeSequenceName, cooperationStatusName, storeExpansionStatusName */}
    <FormSetName name={identification} initialValue={{}}></FormSetName>

    <Col span={12}>
      <V2FormSelect
        name={storeSequenceProp}
        label='门店序列'
        disabled={disabled}
        required={required}
        allowClear={false}
        options={selection.storeSequence}
        onChange={methods.changeStoreSequence}
      />
    </Col>
    <Col span={12}>
      <V2FormSelect
        name={cooperationStatusProp}
        label='合作状态'
        disabled={true}
        required={required}
        options={selection.cooperationStatus}
      />
    </Col>
    <Col span={12}>
      <V2FormSelect
        name={storeExpansionStatusProp}
        label='拓店状态'
        disabled={true}
        options={selection.storeExpansionStatus}
      />
    </Col>
  </Row>);
};

const seqCnMap = {
  1: '首',
  2: '二',
  3: '三',
  4: '四',
  5: '五',
  6: '六',
  7: '七',
  8: '八',
  9: '九',
  10: '十',
  11: '十一',
  12: '十二',
  13: '十三',
  14: '十四',
  15: '十五',
  16: '十六',
  17: '十七',
  18: '十八',
  19: '十九',
  20: '二十',
};

/**
 * 获取门店序列选项
 * @description 如果 value 有值，选项只有 value + 1 的选项；
 * 如果 value 为空（未选择），固定返回选项：首店、二店、三店、四店、五店；
 * 最大到二十店
 */
const getStoreSequenceOptions = (value = 0) => {
  value = Number(value);

  const list = Object.keys(seqCnMap)
    .map((key) => ({ value: Number(key), label: `${seqCnMap[key]}店` }))
    .sort((a, b) => a.value - b.value);

  if (value) {
    // 展示当前和下一个
    return list.filter(item => item.value === value || item.value === value + 1);
  } else {
    // 只展示前5个
    return list.filter(item => [1, 2, 3, 4, 5].includes(item.value));
  }
};

export default Component;
